import { useEffect, useRef } from 'react';

import type { AppTypeT, PerformanceEventExtras } from 'src/types/performance';

import type { PlatformContext } from '@/utils/platformDetection';

import { useGetUser } from '@/hooks/api/useGetUser';
import { getAppEnvironment, getAppVersion } from '@/utils/appInfo';
import { getCurrentPlatformContext } from '@/utils/platformDetection';

interface TimerEntry {
  event_id: string;
  event_name: string;
  timestamp: number;
}

// Module-level Map: survives component unmounts and route navigation.
// This is intentional — bot_join starts in AddAssistantViewContainer and
// completes in useParticipants after a route change.
export const activeTimers = new Map<string, TimerEntry>();

const BACKEND_PLATFORM_TO_APP_TYPE: Record<string, AppTypeT> = {
  microsoft_teams: 'Teams',
  zoom: 'Zoom',
  webex: 'Webex',
  google_meet: 'Google Meet',
};

const CONTEXT_TO_APP_TYPE: Record<PlatformContext, AppTypeT> = {
  teams: 'Teams',
  zoom: 'Zoom',
  webex: 'Webex',
  meet: 'Google Meet',
  web: 'Web',
};

/**
 * Meeting-level events carry the backend's platform id (`microsoft_teams`),
 * app-level ones the AppTypeT label (`Teams`) — normalise so one FullStory
 * filter matches both. `undefined` (caller didn't say) falls back to the route
 * the app runs under; an explicit `null` (platform unknown) stays null.
 */
export function resolvePerfPlatform(
  platform: string | null | undefined,
): string | null {
  if (platform === undefined) {
    return CONTEXT_TO_APP_TYPE[getCurrentPlatformContext()];
  }
  if (platform === null) return null;
  return BACKEND_PLATFORM_TO_APP_TYPE[platform] ?? platform;
}

/**
 * Properties shared by every perf event, including web vitals.
 *
 * `app_env` / `app_build` carry the same values as the identity's
 * `environment` / `app_version` user properties but under distinct names:
 * FullStory metrics can't tell an event property from a same-named user
 * property, and silently filter on the user one (set only after identity).
 */
export function getPerfContextProperties(
  platform?: string | null,
): Record<string, string | null> {
  return {
    platform: resolvePerfPlatform(platform),
    app_env: getAppEnvironment(),
    app_build: getAppVersion(),
  };
}

// Starts that survive a redirect or page load can go stale (a login abandoned
// and resumed hours later, a webview restored from sleep). Drop those rather
// than report day-long latencies that skew every dashboard but p75.
const MAX_LATENCY_MS: Record<string, number> = {
  auth_login: 10 * 60_000,
  app_initial_load: 10 * 60_000,
};

function emitPerfPayload(
  event_name: string,
  event_id: string,
  extras: PerformanceEventExtras,
  user_id: string | null,
  timing: { timestamp: number; latency_ms: number } | null,
): void {
  const payload = {
    event_id,
    event_name,
    ...timing,
    user_id,
    meeting_id: extras.meeting_id ?? null,
    ...extras,
    ...getPerfContextProperties(extras.platform),
  };

  window.FS?.('trackEvent', { name: event_name, properties: payload });

  if (import.meta.env.DEV) {
    console.debug('[perf]', event_name, payload);
  }
}

function emitPerfEvent(
  entry: TimerEntry,
  extras: PerformanceEventExtras,
  user_id: string | null,
): void {
  const latency_ms = Date.now() - entry.timestamp;
  if (latency_ms > (MAX_LATENCY_MS[entry.event_name] ?? Infinity)) return;

  emitPerfPayload(entry.event_name, entry.event_id, extras, user_id, {
    timestamp: entry.timestamp,
    latency_ms,
  });
}

// Fixed key for the page-load timer started in main.tsx before React mounts.
export const INITIAL_LOAD_TIMER_KEY = 'app_initial_load';

// Module-level helpers for use outside React (useWebSocket, useCreateMeetingBot).
// These functions do not have access to user context — caller must pass user_id.

export function startPerfTimer(
  event_name: string,
  fixed_key?: string,
  timestamp: number = Date.now(),
): string {
  const event_id = crypto.randomUUID();
  const key = fixed_key ?? event_id;
  activeTimers.set(key, { event_id, event_name, timestamp });
  return key;
}

export function stopPerfTimer(
  timer_key: string,
  extras: PerformanceEventExtras = {},
  user_id: string | null = null,
  event_name_override?: string,
): void {
  const entry = activeTimers.get(timer_key);
  if (!entry) return;
  activeTimers.delete(timer_key);
  const resolved = event_name_override
    ? { ...entry, event_name: event_name_override }
    : entry;
  emitPerfEvent(resolved, extras, user_id);
}

/** Emit a one-shot perf event from a raw timestamp (no activeTimers entry). */
export function emitOneShotPerfEvent(
  event_name: string,
  timestamp: number,
  extras: PerformanceEventExtras = {},
  user_id: string | null = null,
): void {
  emitPerfEvent(
    { event_id: crypto.randomUUID(), event_name, timestamp },
    extras,
    user_id,
  );
}

/**
 * Emit a perf-family event that records an occurrence but no latency — for
 * flows with no meaningful start point. Carries the shared perf properties so
 * it filters like the timed events.
 */
export function emitPerfCountEvent(
  event_name: string,
  extras: PerformanceEventExtras = {},
  user_id: string | null = null,
): void {
  emitPerfPayload(event_name, crypto.randomUUID(), extras, user_id, null);
}

/** Write auth start timestamp to sessionStorage before Auth0 redirect. */
export function storeAuthPerfStart(): void {
  sessionStorage.setItem(
    'perf_auth_start',
    JSON.stringify({
      event_id: crypto.randomUUID(),
      timestamp: Date.now(),
      // Captured before the redirect: the callback lands outside the
      // platform route, so the route can't tell it afterwards.
      platform: resolvePerfPlatform(undefined),
    }),
  );
}

/** Read auth start from sessionStorage and emit auth_login perf event. */
export function emitAuthPerfEvent(): void {
  const stored = sessionStorage.getItem('perf_auth_start');
  if (!stored) return;
  try {
    const { event_id, timestamp, platform } = JSON.parse(stored) as {
      event_id: string;
      timestamp: number;
      platform?: string | null;
    };
    sessionStorage.removeItem('perf_auth_start');
    emitPerfEvent(
      { event_id, event_name: 'auth_login', timestamp },
      { platform: platform ?? null, meeting_id: null },
      null,
    );
  } catch {
    sessionStorage.removeItem('perf_auth_start');
  }
}

export function cancelPerfTimer(timer_key: string): void {
  activeTimers.delete(timer_key);
}

// React hook — wraps module-level functions and injects user_id from user context
// at stop time (user may not be available when the timer starts).
export function usePerformanceTracking() {
  const { data: user } = useGetUser();

  const startTimer = (event_name: string): string => startPerfTimer(event_name);

  const stopTimer = (
    timer_key: string,
    extras: PerformanceEventExtras = {},
  ): void => {
    // user_id is captured at stop time — user resolves after timer start for
    // early flows like app_initial_load and auth_login.
    stopPerfTimer(timer_key, extras, user?.uuid ?? null);
  };

  const cancelTimer = (timer_key: string): void => cancelPerfTimer(timer_key);

  return { startTimer, stopTimer, cancelTimer };
}

/**
 * Starts a perf timer on mount, stops when `isReady` becomes true.
 * Cancels cleanly on unmount if not yet ready. Fires exactly once.
 *
 * `maxLatencyMs` discards (instead of emitting) a measurement that took longer,
 * so a load that stalled in a backgrounded tab or across laptop sleep doesn't
 * skew averages.
 */
export function useDataLoadTimer(
  event_name: string,
  isReady: boolean,
  extras: PerformanceEventExtras = {},
  { maxLatencyMs }: { maxLatencyMs?: number } = {},
) {
  const { startTimer, stopTimer, cancelTimer } = usePerformanceTracking();
  const timerRef = useRef<string | null>(null);

  useEffect(() => {
    timerRef.current = startTimer(event_name);
    return () => {
      if (timerRef.current) cancelTimer(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isReady && timerRef.current) {
      const entry = activeTimers.get(timerRef.current);
      const isStale =
        maxLatencyMs !== undefined &&
        entry !== undefined &&
        Date.now() - entry.timestamp > maxLatencyMs;

      if (isStale) {
        cancelTimer(timerRef.current);
      } else {
        stopTimer(timerRef.current, extras);
      }
      timerRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]);
}

/**
 * Stops the page-load timer started in main.tsx (backdated to navigation
 * start) once `user` resolves, emitting `app_initial_load`. Measuring from
 * page load rather than from this component's mount matters: in the companion
 * apps the user is usually already cached when the platform view mounts, so a
 * mount-started timer would stop in the same render and report ~0 ms. Fires at
 * most once per page load — the timer entry is removed when stopped (or
 * cancelled when the companion login screen shows), so later mounts are no-ops.
 *
 * `extras` tags the event, e.g. to tell a load that entered through a resumed
 * bot view apart from one that went through the platform's home view.
 */
export function useInitialLoadTimer(
  platform: AppTypeT,
  extras: PerformanceEventExtras = {},
) {
  const { data: user } = useGetUser();
  const { stopTimer } = usePerformanceTracking();

  useEffect(() => {
    if (user) {
      stopTimer(INITIAL_LOAD_TIMER_KEY, {
        ...extras,
        platform,
        meeting_id: null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
}
